import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Typography, Button, Alert } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import http from '../Api/http';
import { GetSalarySecurityStatus } from '../Api/Api';
import { selectModules } from '../redux/slices/authSlice';
import { hasModule } from '../data/serverModules';
import { useSalaryPassphrase, setSalaryPassphrase, clearSalaryPassphrase } from '../utils/salaryPassphrase';
import SecretDialog from './SecretDialog';

// The Encryption Password prompt shared by every salary screen except Salary Structures (which owns the
// full setup / change / recovery flows). One prompt per screen visit; the value lives in memory only and is
// dropped when the screen is left.
export function SalaryUnlockDialog({ open, busy, error, onClose, onSubmit }) {
    return (
        <SecretDialog
            open={open} title="Unlock salary figures" subtitle="Enter the Encryption Password for this entity."
            fields={[{ key: 'pass', label: 'Encryption Password' }]} submitLabel="Unlock"
            busy={busy} error={error} onClose={onClose} onSubmit={({ pass }) => onSubmit(pass)}
        >
            <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>
                Forgot it? Use the recovery key under Payroll Setup &gt; Salary Security.
            </Typography>
        </SecretDialog>
    );
}

// Setup state + unlock prompt for a salary screen.
//   verify(pass)  optional async check; return an error string to reject the passphrase, '' / undefined to accept.
//   onUnlocked()  called after the passphrase changes (accepted, or locked again) so the screen can reload its data.
// Returns the state the screen needs plus `unlockDialog` to render.
export default function useSalaryUnlock({ verify, onUnlocked } = {}) {
    const modules = useSelector(selectModules);
    const canUnlock = hasModule(modules, 'salary-unlock');
    const passphrase = useSalaryPassphrase();
    const [status, setStatus] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const pending = useRef(null);
    const latest = useRef({});
    latest.current = { verify, onUnlocked };

    const loadStatus = useCallback(async () => {
        if (!canUnlock) { setStatus(null); return null; }
        try {
            const res = await http.get(GetSalarySecurityStatus);
            const d = res.data?.data || null;
            setStatus(d);
            return d;
        } catch { setStatus(null); return null; }
    }, [canUnlock]);

    useEffect(() => {
        loadStatus();
        // The passphrase is only ever held while a salary screen is open.
        return () => clearSalaryPassphrase();
    }, [loadStatus]);

    const setupDone = status?.setupCompleted === true;
    const needsPassphrase = setupDone && !passphrase;
    const lockedUntil = status?.pinLockedUntil && new Date(status.pinLockedUntil) > new Date() ? new Date(status.pinLockedUntil) : null;

    // Open the prompt; `then` runs once a passphrase has been accepted (e.g. retry the action that was blocked).
    const requestUnlock = useCallback((then) => {
        pending.current = then || null;
        setError('');
        setOpen(true);
    }, []);

    const close = () => { if (!busy) { setOpen(false); pending.current = null; } };

    const submit = async (pass) => {
        setBusy(true); setError('');
        try {
            const bad = latest.current.verify ? await latest.current.verify(pass) : '';
            if (bad) { setError(bad); loadStatus(); return; }
            setSalaryPassphrase(pass);
            setOpen(false);
            const next = pending.current;
            pending.current = null;
            latest.current.onUnlocked?.();
            if (next) setTimeout(next, 0);
        } finally { setBusy(false); }
    };

    const lockNow = () => { clearSalaryPassphrase(); latest.current.onUnlocked?.(); };

    const unlockDialog = <SalaryUnlockDialog open={open} busy={busy} error={error} onClose={close} onSubmit={submit} />;

    return { canUnlock, passphrase, status, setupDone, needsPassphrase, lockedUntil, loadStatus, requestUnlock, lockNow, unlockDialog };
}

// The strip at the top of a salary screen: locked -> prompt to unlock, unlocked -> lock again.
export function SalaryLockBanner({ unlock, amountsLocked }) {
    const { canUnlock, setupDone, passphrase, needsPassphrase, lockedUntil, requestUnlock, lockNow } = unlock;
    if (needsPassphrase) {
        return (
            <Alert
                severity="warning" icon={<LockOutlinedIcon fontSize="small" />} sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}
                action={<Button size="small" onClick={() => requestUnlock()} sx={{ textTransform: 'none', fontWeight: 700, color: '#92400E' }}>Unlock</Button>}
            >
                Enter the password to view salary figures.{lockedUntil ? ` Locked until ${lockedUntil.toLocaleString()}.` : ''}
            </Alert>
        );
    }
    if (!canUnlock && amountsLocked) {
        return (
            <Alert severity="warning" icon={<LockOutlinedIcon fontSize="small" />} sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}>
                Your role can see this list but is not allowed to unlock salary figures.
            </Alert>
        );
    }
    if (setupDone && passphrase) {
        return (
            <Alert
                severity="success" icon={<LockOpenOutlinedIcon fontSize="small" />} sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}
                action={<Button size="small" onClick={lockNow} sx={{ textTransform: 'none', fontWeight: 700 }}>Lock</Button>}
            >
                Salary figures are unlocked for this screen.
            </Alert>
        );
    }
    return null;
}
